/**
 * CatalogFacetDropdown
 *
 * A single multi-select facet rendered as a chip-style button that
 * pops a click-to-toggle text-link list — same option styling as the
 * old left rail, just horizontal and lazy-loaded into a Popover so
 * the catalog filter bar stays compact when many fields are defined.
 *
 * Selected count appears in the button label as `Label · 2` so the
 * user can tell which facets are filtering without opening each one.
 */

import { useState } from 'react';
import { Box, Button, Popover, Typography } from '../ui';
import { ChevronRightIcon } from '../icons';

export interface CatalogFacetOption {
  value: string;
  count?: number;
}

export interface CatalogFacetDropdownProps {
  label: string;
  options: CatalogFacetOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}

export default function CatalogFacetDropdown({
  label,
  options,
  selected,
  onChange,
}: CatalogFacetDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const hasSelection = selected.length > 0;

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const clearAll = () => onChange([]);

  return (
    <>
      <Button
        size="small"
        variant={hasSelection ? 'contained' : 'outlined'}
        color={hasSelection ? 'primary' : 'inherit'}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={
          <ChevronRightIcon
            sx={{
              transform: open ? 'rotate(-90deg)' : 'rotate(90deg)',
              transition: 'transform 150ms ease',
            }}
          />
        }
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          // Selected state stays in the cyan family so the bar reads
          // as one consistent control set even when several facets
          // are active.
          ...(hasSelection
            ? { color: '#fff' }
            : { borderColor: 'divider', color: 'text.primary' }),
        }}
      >
        {label}
        {hasSelection && (
          <Box
            component="span"
            sx={{
              ml: 0.75,
              px: 0.75,
              borderRadius: 999,
              fontSize: '0.65rem',
              fontWeight: 700,
              bgcolor: 'rgba(255,255,255,0.25)',
            }}
          >
            {selected.length}
          </Box>
        )}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { mt: 0.5, minWidth: 220, maxWidth: 320, p: 1 },
          },
        }}
      >
        {/* Header — clear-all action when something's selected. */}
        {hasSelection && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
              pb: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {selected.length} selected
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={clearAll}
              sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0 }}
            >
              Clear
            </Button>
          </Box>
        )}
        {options.length === 0 ? (
          <Box sx={{ px: 1, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              No values to filter by.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 320, overflow: 'auto', pr: 0.5 }}>
            {options.map((opt) => {
              const isSelected = selected.includes(opt.value);
              return (
                <Box
                  key={opt.value}
                  onClick={() => toggle(opt.value)}
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 0.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'primary.main',
                      fontWeight: isSelected ? 700 : 500,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {opt.value}
                  </Typography>
                  {opt.count != null && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flexShrink: 0 }}
                    >
                      ({opt.count})
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Popover>
    </>
  );
}
